/**
 * Pure scene-timeline math and immutable CRUD over VideoProject.
 *
 * Scene order always follows `Project.pages[]` order: `syncScenesToPages` is
 * the one function that reconciles the two, and it runs whenever pages change
 * and on load. Everything here is DOM-free and Fabric-free.
 */

import {
  VIDEO_MAX_SCENE_MS,
  VIDEO_MAX_TOTAL_MS,
  VIDEO_MAX_TRANSITION_MS,
  VIDEO_MIN_SCENE_MS,
  type ProjectPage,
  type SceneCue,
  type SceneTransition,
  type VideoExportSettings,
  type VideoProject,
  type VideoScene,
} from '@canvas/shared-types';
import { isHardCut } from '@/data/transitions';

export const DEFAULT_SCENE_MS = 3000;

export const DEFAULT_TRANSITION: SceneTransition = { id: 'cut', durationMs: 0 };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Clamp a scene duration to the allowed range, rounded to whole ms. */
export function clampSceneMs(ms: number): number {
  if (!Number.isFinite(ms)) return DEFAULT_SCENE_MS;
  return Math.round(clamp(ms, VIDEO_MIN_SCENE_MS, VIDEO_MAX_SCENE_MS));
}

/** New scene for a page, optionally inheriting timing from a neighbour. */
export function createScene(pageId: string, template?: VideoScene): VideoScene {
  return {
    v: 1,
    pageId,
    durationMs: template ? template.durationMs : DEFAULT_SCENE_MS,
    transitionIn: template ? { ...template.transitionIn } : { ...DEFAULT_TRANSITION },
  };
}

/** H.264 4:2:0 requires even dimensions. */
function evenDim(x: number): number {
  return Math.max(2, Math.round(x / 2) * 2);
}

/** Default export settings sized to the artboard (long edge capped at 1920). */
export function defaultExportSettings(artboard: {
  width: number;
  height: number;
}): VideoExportSettings {
  const w = artboard.width || 1080;
  const h = artboard.height || 1920;
  const scale = Math.min(1, 1920 / Math.max(w, h));
  return {
    v: 1,
    platformId: 'custom',
    width: evenDim(w * scale),
    height: evenDim(h * scale),
    fps: 30,
    videoBitrate: 8_000_000,
    container: 'mp4',
    videoCodec: 'avc',
    includeAudio: true,
    audioBitrate: 128_000,
    audioSampleRate: 48000,
    backend: 'auto',
    keyFrameInterval: 60,
    quality: 'standard',
  };
}

/** A fresh, enabled timeline with one default scene per page. */
export function createVideoProject(
  pages: ProjectPage[],
  artboard: { width: number; height: number },
  now: number
): VideoProject {
  return withDerived({
    v: 1,
    enabled: true,
    scenes: pages.map((p) => createScene(p.pageId)),
    audio: null,
    exportSettings: defaultExportSettings(artboard),
    totalDurationMs: 0,
    updatedAt: now,
  });
}

/**
 * Effective overlap of a transition between two scenes. Capped at half of
 * either scene so a long transition can never swallow a short scene whole.
 */
function overlapMs(prev: VideoScene, cur: VideoScene): number {
  if (isHardCut(cur.transitionIn.id)) return 0;
  const want = clamp(cur.transitionIn.durationMs || 0, 0, VIDEO_MAX_TRANSITION_MS);
  return Math.round(Math.min(want, prev.durationMs / 2, cur.durationMs / 2));
}

/** `m:ss.t` for timeline labels, e.g. 75_300 → "1:15.3". */
export function formatTimecode(ms: number): string {
  const tenths = Math.max(0, Math.floor(ms / 100));
  const minutes = Math.floor(tenths / 600);
  const seconds = Math.floor((tenths % 600) / 10);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${tenths % 10}`;
}

/** Resolve each scene's window on the global timeline. */
export function buildCues(scenes: VideoScene[]): SceneCue[] {
  const cues: SceneCue[] = [];
  let cursor = 0;
  scenes.forEach((scene, index) => {
    const overlapIn = index === 0 ? 0 : overlapMs(scenes[index - 1], scene);
    const startMs = Math.max(0, cursor - overlapIn);
    const endMs = startMs + scene.durationMs;
    if (index > 0) cues[index - 1].overlapOutMs = overlapIn;
    cues.push({
      pageId: scene.pageId,
      index,
      startMs,
      endMs,
      overlapInMs: overlapIn,
      overlapOutMs: 0,
    });
    cursor = endMs;
  });
  return cues;
}

/** Total timeline length: the end of the last cue. */
export function totalDurationMs(scenes: VideoScene[]): number {
  const cues = buildCues(scenes);
  return cues.length ? cues[cues.length - 1].endMs : 0;
}

/**
 * The cue showing at `ms`. Inside a transition overlap this is the INCOMING
 * scene, since that is the one the playhead is moving into.
 */
export function cueAt(cues: SceneCue[], ms: number): SceneCue | null {
  if (!cues.length) return null;
  let found: SceneCue = cues[0];
  for (const cue of cues) {
    if (cue.startMs <= ms) found = cue;
    else break;
  }
  return found;
}

/** Time relative to a cue's start, clamped to the scene. */
export function sceneLocalMs(cue: SceneCue, ms: number): number {
  return clamp(ms - cue.startMs, 0, cue.endMs - cue.startMs);
}

export interface ActiveTransition {
  from: SceneCue;
  to: SceneCue;
  transition: SceneTransition;
  /** Linear 0..1 through the overlap; the renderer applies easing. */
  progress: number;
}

/** The transition in progress at `ms`, or null when a single scene is showing. */
export function activeTransitionAt(
  cues: SceneCue[],
  scenes: VideoScene[],
  ms: number
): ActiveTransition | null {
  for (let i = 1; i < cues.length; i += 1) {
    const to = cues[i];
    if (to.overlapInMs <= 0) continue;
    const from = cues[i - 1];
    if (ms >= to.startMs && ms < from.endMs) {
      return {
        from,
        to,
        transition: scenes[i].transitionIn,
        progress: (ms - to.startMs) / to.overlapInMs,
      };
    }
  }
  return null;
}

/** Recompute denormalized fields. Every CRUD helper returns through this. */
export function withDerived(video: VideoProject): VideoProject {
  if (video.v === 2 && video.clips) {
    const total = video.clips.reduce((max, clip) => Math.max(max, clip.startMs + clip.durationMs), 0);
    return { ...video, totalDurationMs: total };
  }
  return { ...video, totalDurationMs: totalDurationMs(video.scenes) };
}

/* ---------- CRUD (immutable; mirrors projectPages.ts) ---------- */

/** Insert a scene at `index` (default: end). */
export function addScene(video: VideoProject, scene: VideoScene, index?: number): VideoProject {
  const scenes = video.scenes.slice();
  const at = index === undefined ? scenes.length : clamp(index, 0, scenes.length);
  scenes.splice(at, 0, scene);
  return withDerived({ ...video, scenes });
}

export function removeScene(video: VideoProject, pageId: string): VideoProject {
  const scenes = video.scenes.filter((s) => s.pageId !== pageId);
  if (scenes.length === video.scenes.length) return video;
  return withDerived({ ...video, scenes });
}

/** Move by delta (-1 left, +1 right). Returns the same object if no-op. */
export function moveScene(video: VideoProject, pageId: string, delta: number): VideoProject {
  const from = video.scenes.findIndex((s) => s.pageId === pageId);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= video.scenes.length) return video;
  const scenes = video.scenes.slice();
  const [item] = scenes.splice(from, 1);
  scenes.splice(to, 0, item);
  return withDerived({ ...video, scenes });
}

/** Reorder by dragging `fromId` onto `toId` (insert before target). */
export function reorderScene(video: VideoProject, fromId: string, toId: string): VideoProject {
  if (fromId === toId) return video;
  const from = video.scenes.findIndex((s) => s.pageId === fromId);
  if (from < 0 || !video.scenes.some((s) => s.pageId === toId)) return video;
  const scenes = video.scenes.slice();
  const [item] = scenes.splice(from, 1);
  const insertAt = scenes.findIndex((s) => s.pageId === toId);
  scenes.splice(insertAt < 0 ? scenes.length : insertAt, 0, item);
  return withDerived({ ...video, scenes });
}

/**
 * Set a scene's duration, clamped to the per-scene range and to whatever is
 * left of the total-length budget.
 */
export function setSceneDuration(video: VideoProject, pageId: string, ms: number): VideoProject {
  const target = video.scenes.find((s) => s.pageId === pageId);
  if (!target) return video;
  const others = totalDurationMs(video.scenes) - target.durationMs;
  const budget = Math.max(VIDEO_MIN_SCENE_MS, VIDEO_MAX_TOTAL_MS - others);
  const durationMs = Math.min(clampSceneMs(ms), budget);
  if (durationMs === target.durationMs) return video;
  const scenes = video.scenes.map((s) => (s.pageId === pageId ? { ...s, durationMs } : s));
  return withDerived({ ...video, scenes });
}

/**
 * Scene-local time the strip poster shows. An explicit `thumbAtMs` wins;
 * otherwise a beat after the start, so an intro animation isn't the poster.
 */
export function scenePosterMs(scene: VideoScene): number {
  if (typeof scene.thumbAtMs === 'number' && Number.isFinite(scene.thumbAtMs)) {
    return Math.min(scene.durationMs, Math.max(0, Math.round(scene.thumbAtMs)));
  }
  return Math.min(400, Math.round(scene.durationMs / 2));
}

/** Remember which frame the strip poster was captured from. */
export function setSceneThumbAt(video: VideoProject, pageId: string, atMs: number): VideoProject {
  const scene = video.scenes.find((s) => s.pageId === pageId);
  if (!scene) return video;
  const thumbAtMs = Math.min(scene.durationMs, Math.max(0, Math.round(atMs)));
  if (scene.thumbAtMs === thumbAtMs) return video;
  const scenes = video.scenes.map((s) => (s.pageId === pageId ? { ...s, thumbAtMs } : s));
  return withDerived({ ...video, scenes });
}

export function setTransition(
  video: VideoProject,
  pageId: string,
  transition: SceneTransition
): VideoProject {
  const next: SceneTransition = {
    ...transition,
    durationMs: Math.round(clamp(transition.durationMs || 0, 0, VIDEO_MAX_TRANSITION_MS)),
  };
  const scenes = video.scenes.map((s) =>
    s.pageId === pageId ? { ...s, transitionIn: next } : s
  );
  return withDerived({ ...video, scenes });
}

/**
 * Reconcile scenes with pages: same order, one scene per page. Scenes for
 * deleted pages are dropped; new pages get a scene that inherits timing from
 * the scene before it (so a duplicated page keeps its duration). Returns the
 * same object when already in sync, so callers can skip writes.
 */
export function syncScenesToPages(video: VideoProject, pages: ProjectPage[]): VideoProject {
  // Clip timelines own their own timing. Rebuilding the scene lane would
  // throw away text, music, and template clips.
  if (video.v === 2) return video;
  const inSync =
    video.scenes.length === pages.length &&
    video.scenes.every((s, i) => s.pageId === pages[i].pageId);
  if (inSync) return video;

  const byId = new Map(video.scenes.map((s) => [s.pageId, s]));
  const scenes: VideoScene[] = [];
  pages.forEach((page) => {
    const existing = byId.get(page.pageId);
    if (existing) {
      scenes.push(existing);
      return;
    }
    const prev = scenes[scenes.length - 1];
    scenes.push(createScene(page.pageId, prev));
  });
  return withDerived({ ...video, scenes });
}
